import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_ME, UPDATE_USER_COLOR } from '../utils/mutations';

function Profile() {
  const [color, setColor] = useState('#462b65');
  const [saveStatus, setSaveStatus] = useState('');
  const statusTimerRef = useRef(null);

  const { data } = useQuery(GET_ME);
  const [updateColor] = useMutation(UPDATE_USER_COLOR, {
    refetchQueries: [{ query: GET_ME }]
  });

  useEffect(() => {
    if (data?.me?.iconColor) {
      setColor(data.me.iconColor);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (statusTimerRef.current) {
        window.clearTimeout(statusTimerRef.current);
      }
    };
  }, []);

  const handleColorChange = (event) => {
    setColor(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await updateColor({ variables: { color } });
      setSaveStatus('saved');

      if (statusTimerRef.current) {
        window.clearTimeout(statusTimerRef.current);
      }

      statusTimerRef.current = window.setTimeout(() => {
        setSaveStatus('');
      }, 2000);
    } catch (err) {
      console.error('Failed to save color:', err);
      setSaveStatus('error');
    }
  };

  const saveStatusText =
    saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Error saving color' : '';
  const saveStatusClass =
    saveStatus === 'saved' ? 'text-emerald-300' : saveStatus === 'error' ? 'text-red-300' : '';

  return (
    <main className="mx-auto flex w-full text-center max-w-2xl flex-col gap-5 p-5 sm:p-6">
        <h1 className="text-2xl font-bold text-slate-100">Profile</h1>


      <section className="rounded-2xl border border-white/15 bg-slate-900/55 p-4 sm:p-5">
        <h3 className="text-base font-semibold text-slate-100">Marker Color</h3>
        <p className="mt-1 text-sm text-slate-300">
          Choose the color shown on your map markers.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-center gap-3">
            <label htmlFor="iconColor" className="text-sm font-medium text-slate-200">
              Color
            </label>
            <input
              id="iconColor"
              type="color"
              value={color}
              onChange={handleColorChange}
              className="h-10 w-10 cursor-pointer rounded-md border border-white/25 bg-transparent p-0"
            />
            <span className="rounded-md border border-white/15 bg-slate-800/70 px-2 py-1 text-xs uppercase tracking-wide text-slate-300">
              {color}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" className="btn-active px-4 py-2">
              Save
            </button>
            <span
              aria-live="polite"
              className={`min-h-[1.25rem] text-sm font-medium ${saveStatusClass}`}
            >
              {saveStatusText}
            </span>
          </div>
        </form>
      </section>

      <section className="rounded-2xl border border-white/15 bg-slate-900/55 p-4 sm:p-5">
        <h3 className="text-base font-semibold text-slate-100">ID.me Verification</h3>
        <p className="mt-1 text-sm text-slate-300">
          Connect your account with ID.me to display a verified badge when eligible.
        </p>
        <p className="mt-2 text-sm text-slate-300">
          Eligible groups include teachers, military members and veterans, nurses, and first
          responders. We do not store names or location details from ID.me.
        </p>
        <p className="mt-2 text-sm text-slate-300">
          ID.me confirms identity before granting access so others cannot impersonate you.
        </p>

        <div className="mt-4 flex flex-col items-center gap-3">
          <a
            className="text-xs text-slate-300 underline decoration-slate-400/60 underline-offset-2"
            href="https://www.id.me/about"
            target="_blank"
            rel="noreferrer"
          >
            Learn more about ID.me
          </a>
          <a href="https://groups.id.me/?client_id=9c5df2988ffbe63c12afdd0535023cf2&redirect_uri=https://www.projectcivilian.org/idme-callback&response_type=code&scopes=government,military,nurse,responder,teacher">
            <img
              style={{ cursor: 'pointer', width: '200px' }}
              src="https://s3.amazonaws.com/idme/buttons/v4/verify-with-idme-green.png"
              alt="Verify with ID.me"
            />
          </a>
        </div>
      </section>
    </main>
  );
}

export default Profile;
